import React, { useCallback, useEffect, useRef, useState } from "react";
import { Upload, Trash2, Search, FileSpreadsheet } from "lucide-react";
import { Panel, MonoLabel } from "@/components/Panel";
import { api, errText } from "@/lib/devadminApi";
import { toast } from "sonner";

export function MemberDirectory({ eventId }) {
  const [data, setData] = useState(null);
  const [chapter, setChapter] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/events/${eventId}/member-directory`, {
        params: { ...(chapter ? { chapter } : {}), ...(q.trim().length >= 2 ? { q: q.trim() } : {}) },
      });
      setData(d);
    } catch (e) { toast.error(errText(e)); }
  }, [eventId, chapter, q]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setReport(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: r } = await api.post(`/events/${eventId}/member-directory/import`, fd);
      setReport(r);
      toast.success(`${r.inserted} added · ${r.updated} updated · ${r.skipped} skipped`);
      load();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const clearAll = async () => {
    if (!window.confirm("Delete the entire member directory for this event?")) return;
    setBusy(true);
    try {
      const { data: r } = await api.delete(`/events/${eventId}/member-directory`);
      toast.success(`${r.deleted} entries removed`);
      setReport(null); load();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const removeRow = async (id) => {
    try { await api.delete(`/member-directory/${id}`); load(); }
    catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="space-y-6">
      <Panel className="p-6">
        <MonoLabel><FileSpreadsheet size={13} className="inline mr-2" />Master member directory</MonoLabel>
        <p className="text-sm mt-3" style={{ color: "var(--taupe)" }}>
          Upload a .csv or .xlsx with the columns <code>chapter</code>, <code>name</code>, <code>phone</code> and
          optionally <code>email</code>. Members are matched on phone, so re-uploading an updated sheet is safe.
          Sponsors pick their chapter and their own name from this list on the registration page.
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <label className="btn-red cursor-pointer">
            <Upload size={14} /> {busy ? "Working…" : "Upload sheet"}
            <input ref={fileRef} data-testid="directory-upload-input" type="file" className="hidden"
              accept=".csv,.xlsx,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              disabled={busy} onChange={(e) => upload(e.target.files?.[0])} />
          </label>
          <button type="button" className="btn-ghost" data-testid="directory-clear-btn" disabled={busy || !data?.total} onClick={clearAll}>
            <Trash2 size={14} /> Clear directory
          </button>
          <MonoLabel style={{ color: "var(--taupe)" }} data-testid="directory-total">
            {data ? `${data.total} members · ${data.chapters.length} chapters` : "Loading…"}
          </MonoLabel>
        </div>

        {report && (
          <div className="mt-5 p-4 hairline text-sm" data-testid="directory-import-report" style={{ background: "var(--panel-2)" }}>
            <div className="mono-label">Last import</div>
            <div className="mt-2">{report.inserted} added · {report.updated} updated · {report.skipped} skipped · {report.total} total</div>
            {report.errors?.length > 0 && (
              <ul className="mt-3 space-y-1" style={{ color: "var(--red-2)" }}>
                {report.errors.map((x, i) => <li key={i} className="mono-label">Row {x.row}: {x.message}</li>)}
              </ul>
            )}
          </div>
        )}
      </Panel>

      <Panel className="p-6">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <MonoLabel>Directory</MonoLabel>
          <div className="flex flex-wrap items-center gap-3">
            <select data-testid="directory-chapter-filter" className="input-x !w-auto !py-2" value={chapter} onChange={(e) => setChapter(e.target.value)}>
              <option value="">All chapters</option>
              {(data?.chapters || []).map((c) => <option key={c.chapter} value={c.chapter}>{c.chapter} ({c.members})</option>)}
            </select>
            <div className="flex items-center gap-2">
              <Search size={14} style={{ color: "var(--taupe)" }} />
              <input data-testid="directory-search" className="input-x !w-auto !py-2" placeholder="Name or phone" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
          </div>
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left mono-label">
              <th className="py-3 pr-3">Chapter</th><th className="py-3 pr-3">Name</th>
              <th className="py-3 pr-3">Phone</th><th className="py-3 pr-3">Email</th><th className="py-3"></th>
            </tr></thead>
            <tbody>
              {!data && <tr><td colSpan={5} className="py-6 mono-label">Loading…</td></tr>}
              {data && data.items.length === 0 && (
                <tr><td colSpan={5} className="py-6 mono-label" style={{ color: "var(--taupe)" }} data-testid="directory-empty">
                  Nothing here yet — upload your member sheet above.
                </td></tr>
              )}
              {(data?.items || []).map((r, i) => (
                <tr key={r.id} className="hairline-t" data-testid={`directory-row-${i}`}>
                  <td className="py-3 pr-3 mono-label">{r.chapter}</td>
                  <td className="py-3 pr-3">{r.name}</td>
                  <td className="py-3 pr-3 mono-label">{r.phone}</td>
                  <td className="py-3 pr-3 mono-label" style={{ color: "var(--taupe)" }}>{r.email || "—"}</td>
                  <td className="py-3 text-right">
                    <button className="mono-label" style={{ color: "var(--red-2)" }} data-testid={`directory-del-${i}`} onClick={() => removeRow(r.id)}>
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
